import { convertQueryParamMapToString } from '../route-provider'

export {
  getApplicationOrgId,
  getApplicationDrilldownID,
  getServerDrilldownID,
  getVulnerabilityDrilldownIDFromLocation as getVulnerabilityDrilldownID,
  getApplicationVulnerabilityDrilldownIDFromLocation as getApplicationVulnerabilityDrilldownID,
  getServerVulnerabilityDrilldownIDFromLocation as getServerVulnerabilityDrilldownID,
  getRouteParams
} from './current-route'

export { parseSearchStringFromHash } from './parse-route'

export function updateRoute(hash: string) {
  if (document.location) {
    document.location.hash = hash
  }
}

export function locationAssign(url: string) {
  if (window.location) {
    window.location.assign(url)
  }
}

function getOrganizationIDFromCurrentRoute() {
  return window.location.hash.split('/')[1]
}

function getBaseOrganizationRoute(organizationID: string) {
  return `#/${organizationID}`
}

function getBaseSuperadminRoute() {
  return `#/superadmin`
}

export function getWizardRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/wizard/download/JAVA`
}

export function getSensorWizardRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/scans/onboard`
}

export function getSelectSensorWizardRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/select-sensor-wizard`
}

export function getApplicationsRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/applications`
}

export function getProjectsRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/projects`
}

export function getVulnerabilitiesRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/vulns`
}

export function getLibrariesRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/libraries`
}

export function getAttacksRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/attacks/attacks`
}

export function getAssessRulesRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/rules`
}

export function getSensitiveDataRoute() {
  return `${getBaseOrganizationRoute(
    getOrganizationIDFromCurrentRoute()
  )}/rules/sensitive`
}

export function getApplicationDrilldownRoute(
  organizationID: string,
  applicationID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}`
}

export function getApplicationVulnerabilitiesRoute(
  organizationID: string,
  applicationID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/vulns`
}

export function getApplicationFlowMapRoute(
  organizationID: string,
  applicationID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/flow-map`
}

export function getApplicationLibrariesRoute(
  organizationID: string,
  applicationID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/libs`
}

export function getAgentUserDetailsRoute(
  organizationID: string,
  userId: string
) {
  return `${getBaseOrganizationRoute(organizationID)}/settings/api/${userId}`
}

export function getAgentApiSettings(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/settings/api`
}

export function getApplicationLibrariesWithPanelOpenRoute(
  organizationID: string,
  applicationID: string,
  libraryHash: string,
  language: string,
  libraryName: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/libs?filterText=${libraryName}&showPanel=true&activeRow=${libraryHash}|${language}`
}

export function getApplicationRouteCoverageRoute(
  organizationID: string,
  applicationID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/route-coverage`
}

export function getApplicationUrlActivityCoverage(
  organizationID: string,
  applicationID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/coverage`
}

export function getApplicationExclusionsRoute(
  organizationID: string,
  applicationID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/exclusions`
}

export function getServersFilteredRoute(
  organizationUuid: string,
  appId: string,
  env: string
) {
  return `${getBaseOrganizationRoute(
    organizationUuid
  )}/servers?applications=${appId}&environments=${env}`
}

export function getServerDrilldownRoute(
  organizationID: string,
  serverID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/servers/${serverID}/overview`
}

export function getVulnerabilityDrilldownRoute(
  organizationID: string,
  vulnerabilityID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/vulns/${vulnerabilityID}/overview`
}

export function getVulnerabilityDiscussionRoute(
  organizationID: string,
  vulnerabilityID: string
) {
  return `${getVulnerabilityDrilldownRoute(
    organizationID,
    vulnerabilityID
  )}/discussion`
}

export function getApplicationVulnerabilityDrilldownRoute(
  organizationID: string,
  applicationID: string,
  vulnerabilityID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/vulns/${vulnerabilityID}`
}

export function getServerVulnerabilityDrilldownRoute(
  organizationID: string,
  serverID: string,
  vulnerabilityID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/servers/${serverID}/vulns/${vulnerabilityID}`
}

export function getServerVulnerabilitiesDrilldownRoute(
  organizationID: string,
  serverID: string
) {
  return `${getBaseOrganizationRoute(organizationID)}/servers/${serverID}/vulns`
}

export function getLibraryDrilldownRoute(
  organizationID: string,
  language: string,
  libraryHash: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/libraries/${language.toLowerCase()}/${libraryHash}`
}

export function getApplicationVulnerabilityRoute(
  organizationID: string,
  applicationID: string,
  filters?: Map<string, string[] | string>
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/applications/${applicationID}/vulns${applyFilters(filters)}`
}

export function getCompliancePolicyEditRoute(
  organizationID: string,
  policyID: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/rules/compliance/policy/edit/${policyID}`
}

export function getSourceNameEditRoute(
  organizationID: string,
  sourceNameId: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/rules/ip/source-names/edit/${sourceNameId}`
}

export function getAccountsRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/account`
}

export function getAccountNotificationsRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/account/notifications`
}

export function getSuperadminAccountsRoute() {
  return `${getBaseSuperadminRoute()}/account`
}

function applyFilters(filters?: Map<string, string[] | string>) {
  return filters ? `?${convertQueryParamMapToString(filters)}` : ''
}

export function getCreateVirtualPathRoute(
  organizationID: string,
  eventUuid: string
) {
  return `${getBaseOrganizationRoute(
    organizationID
  )}/rules/patches/new/${eventUuid}`
}

export function getProtectRulesRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/rules/protection`
}

export function getCveShieldsRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/rules/cve`
}

export { mockCurrentRoute } from './mock-current-route'

export function getAgentKeysRoute() {
  return `${getBaseOrganizationRoute(
    getOrganizationIDFromCurrentRoute()
  )}/settings/api`
}

export function getIntegrationsRoute() {
  return `${getBaseOrganizationRoute(
    getOrganizationIDFromCurrentRoute()
  )}/settings/integration`
}

export function getIntegrationsManageRoute() {
  return `${getBaseOrganizationRoute(
    getOrganizationIDFromCurrentRoute()
  )}/settings/integration/credentials`
}

export function getRulesExclusionsRoute(organizationID: string) {
  return `${getBaseOrganizationRoute(organizationID)}/rules/exclusions`
}

export const SCM_ONBOARDING_ROUTE_PATH = '/scm-onboarding'
export const SCM_OPTIONS_ROUTE_PATH = '/scm-options'
